import { ContactsSearchResult } from '@mm/state/actions/LiveChatCRUDActions';
import { useActor } from '@xstate/react';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useContextSelector } from 'use-context-selector';
import Spinner from '../../../../../../common/components/Spinner';
import { LiveChatContext } from '../../../LiveChatContext';
import { LcSearchStates } from '../../../liveChatMachine';
import { ChannelIcon } from '../../ChannelIcon';
import { ProfileImage } from '../../ProfileImage/ProfileImage';
import './shared.pcss';

type Props = {
  selectedContactsContactId: string | undefined;
  selectedConversationsContactId: string | undefined;
  setSelectedContactsContactId: (id: string | undefined) => void;
  setSelectedConversationsContactId: (id: string | undefined) => void;
};

export const FoundContacts = (props: Props) => {
  const {
    selectedContactsContactId,
    setSelectedContactsContactId,
    setSelectedConversationsContactId,
  } = props;

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [lcMachineState, lcMachineSend] = useActor(lcService);

  const clearSearchResultsMessages = () =>
    lcMachineSend({
      type: 'LC_CONTEXT_UPDATE',
      payload: {
        searchResultsMessages: [],
      },
    });

  const { foundContacts: contacts } = lcMachineState.context;

  const isSearchingContacts = lcMachineState.matches({
    mainLogic: { active: { contactSearch: LcSearchStates.searching } },
  });

  const [showMore, setShowMore] = useState(false);
  const [contactsToShow, setContactsToShow] = useState(contacts);

  useEffect(() => {
    const moreThanFive = contacts.length > 5;
    setShowMore(moreThanFive);
    setContactsToShow(moreThanFive ? contacts.slice(0, 5) : contacts);
  }, [contacts]);

  const onClickShowMore = () => {
    setShowMore(false);
    setContactsToShow(contacts);
  };

  const onContactClick = (id: string) => {
    clearSearchResultsMessages();
    setSelectedConversationsContactId(undefined);
    setSelectedContactsContactId(id);

    lcMachineSend({ type: 'LC_REMOTE_USER_ID_CHANGE', payload: id });
  };

  const generateName = (contact: ContactsSearchResult) => {
    if (contact.firstName && contact.lastName && contact.username) {
      if (
        contact.firstName === 'Instagram Contact' &&
        contact.lastName === 'IG' &&
        contact.username
      ) {
        return `@${contact.username}`;
      }
      return `${contact.firstName} ${contact.lastName} (@${contact.username})`;
    }
    if (contact.firstName && contact.lastName) {
      return `${contact.firstName} ${contact.lastName}`;
    }
    if (contact.firstName) {
      return `${contact.firstName}`;
    }
    if (contact.lastName) {
      return `${contact.lastName}`;
    }
    if (contact.username) {
      return `@${contact.username}`;
    }
  };

  return (
    <div>
      <div className="lc-found-contacts__title">Contacts</div>
      <Spinner
        show={isSearchingContacts}
        size={15}
        position="relative"
        backgroundColor="transparent"
      />
      {!isSearchingContacts && contactsToShow?.length === 0 && (
        <div className="lc-found-contacts__empty">No contacts found.</div>
      )}
      {!isSearchingContacts && contactsToShow && contactsToShow.length > 0 && (
        <>
          <div className="lc-search-results--container">
            {contactsToShow.map(c => (
              <div
                className={classnames(
                  'lc-search-result__container',
                  'lc-search-result__container--contact',
                  {
                    'lc-search-result__container--selected':
                      c.remoteUserId === selectedContactsContactId,
                  },
                )}
                key={c.remoteUserId}
                onClick={() => onContactClick(c.remoteUserId)}
              >
                <ProfileImage
                  profilePictureUrl={c.profilePic}
                  altText="Profile Image"
                />
                <div className="lc-found-contact--channel-and-name-container">
                  <ChannelIcon
                    remoteUserId={c.remoteUserId}
                    className="lc-found-contact--channel"
                  />
                  <div className="lc-found-contact--name">
                    {generateName(c)}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div>
            {showMore && (
              <div
                className="lc-search-result__more-link"
                onClick={onClickShowMore}
              >
                {`${contacts.length - 5} more contacts`}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
